import React, { useRef, useEffect, useState, useCallback, FC } from "react";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import LoadingMessageService from "../../../../services/utility/LoadingMessageService";
import "./styles/LoadingOverlay.css";

// @TODO : remove all the functionality as the loading overlay css is changed

// --- Global Configuration & Timing Constants for LoadingOverlay ---
const THICKNESS = 12.0;
const CORE_COLOR = "#00FFFF"; // Hyper-Aqua Energy
const CORE_RGB = "0, 255, 255";
const BG_OVERLAY_COLOR = "rgba(10, 25, 40, 0.1)";
const SEGMENT_LENGTH_RATIO = 0.2;

// Animation timing constants (all in milliseconds)
const T_SEGMENT = 400;
const T_STOP = 100;
const PRELUDE_DURATION = T_SEGMENT; // 400ms initial wipe (TL to TR)
const T_CYCLE = 4 * T_SEGMENT + 4 * T_STOP; // 2000ms per full loop

// Easing function for acceleration (Quintic)
const easeInQuint = (t: number): number => t * t * t * t * t;

/**
 * Calculates (x, y) coordinates for a given distance along the viewport perimeter.
 */
const calculatePerimeterCoords = (
  pos: number,
  W: number,
  H: number,
): { x: number; y: number } => {
  const L = 2 * W + 2 * H;
  pos %= L;

  if (pos <= W) return { x: pos, y: 0 }; // Top edge (TL to TR)
  if (pos <= W + H) return { x: W, y: pos - W }; // Right edge (TR to BR)
  if (pos <= 2 * W + H) return { x: W - (pos - (W + H)), y: H }; // Bottom edge (BR to BL)
  return { x: 0, y: H - (pos - (2 * W + H)) }; // Left edge (BL to TL)
};

/**
 * Renders the single Chrono-Filament (the glowing "ball" and its comet trail).
 */
const renderChronoFilament = (
  ctx: CanvasRenderingContext2D,
  W: number,
  H: number,
  headPos: number,
  L: number,
): void => {
  const segmentLength = L * SEGMENT_LENGTH_RATIO;
  const tailPos = headPos - segmentLength;
  const step = 2;

  ctx.lineWidth = THICKNESS;
  ctx.lineCap = "round";

  // Draw the comet trail (fading light effect)
  for (let currentDist = tailPos; currentDist < headPos; currentDist += step) {
    let normalizedCurrent = currentDist;
    let normalizedNext = currentDist + step;

    while (normalizedCurrent < 0) normalizedCurrent += L;
    normalizedCurrent %= L;
    while (normalizedNext < 0) normalizedNext += L;
    normalizedNext %= L;

    const { x: x1, y: y1 } = calculatePerimeterCoords(normalizedCurrent, W, H);
    const { x: x2, y: y2 } = calculatePerimeterCoords(normalizedNext, W, H);

    const opacity = Math.pow((currentDist - tailPos) / segmentLength, 2.5);
    ctx.strokeStyle = `rgba(${CORE_RGB}, ${opacity})`;

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }

  // Draw the "ball" head with maximum glow
  const { x: headX, y: headY } = calculatePerimeterCoords(headPos, W, H);
  ctx.beginPath();
  ctx.arc(headX, headY, THICKNESS * 2, 0, Math.PI * 2);
  ctx.fillStyle = CORE_COLOR;
  ctx.shadowColor = CORE_COLOR;
  ctx.shadowBlur = 20; // Maximum luminous radiance
  ctx.fill();
  ctx.shadowBlur = 0; // Reset shadow for subsequent draws
};

// --- Component 2: LoadingOverlay Component Definition ---

interface LoadingOverlayProps {}

/**
 * Renders the full-screen loading overlay with background blur and border animation.
 */
const LoadingOverlay: FC<LoadingOverlayProps> = () => {
  const { showLoadingOverlay, loadingMessage } = useNotification();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const startTimeRef = useRef<number>(performance.now());
  const [isCanvasReady, setIsCanvasReady] = useState<boolean>(false);
  const [animationState, setAnimationState] = useState<
    "IDLE" | "PRELUDE" | "LOOP"
  >("IDLE");
  
  // New state for playful messages and pulsing circles
  const [currentMessage, setCurrentMessage] = useState<string>("");
  const [pulseStep, setPulseStep] = useState<number>(0);
  const messageServiceRef = useRef<LoadingMessageService | null>(null);

  // 1. Control animation state based on external prop
  useEffect(() => {
    if (showLoadingOverlay && animationState === "IDLE") {
      setAnimationState("PRELUDE");
      startTimeRef.current = performance.now();
    } else if (!showLoadingOverlay && animationState !== "IDLE") {
      setAnimationState("IDLE");
    }
  }, [showLoadingOverlay, animationState]);

  // 2. Initialize message service and rotation when overlay shows
  useEffect(() => {
    if (showLoadingOverlay) {
      const service = new LoadingMessageService(loadingMessage);
      messageServiceRef.current = service;
      
      // Set initial message
      const initialMsg = service.getInitialMessage();
      setCurrentMessage(initialMsg);
      setPulseStep(0);
      
      // Independent pulse animation (double speed of message rotation)
      const pulseInterval = setInterval(() => {
        setPulseStep(prev => (prev + 1) % 5);
      }, 450); // Pulses every 450ms (double speed of 900ms word change)
      
      // Message rotation (slower)
      const rotationCleanup = service.startRotation((newMsg, step) => {
        setCurrentMessage(newMsg);
        // Don't update pulseStep here - it runs independently
      }, 900); // Words change every 900ms
      
      return () => {
        clearInterval(pulseInterval);
        rotationCleanup();
        messageServiceRef.current = null;
      };
    } else {
      setCurrentMessage("");
      setPulseStep(0);
    }
  }, [showLoadingOverlay, loadingMessage]);

  // 3. Animation and Canvas Setup Loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !showLoadingOverlay || animationState === "IDLE") return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId: number;

    const resizeCanvas = (): void => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      setIsCanvasReady(true);
      // Reset start time on resize to restart animation phase gracefully,
      // only if the animation is currently running (PRELUDE or LOOP).
      if (animationState === "PRELUDE" || animationState === "LOOP") {
        startTimeRef.current = performance.now();
      }
    };

    window.addEventListener("resize", resizeCanvas);
    resizeCanvas();

    // Phasing constants
    const P = T_SEGMENT;
    const S = T_STOP;

    interface Phase {
      s: number;
      e: number;
      l: number;
      p: number;
    }

    const getPhases = (W: number, H: number, L: number): Phase[] => [
      // 0. Move Top (TL to TR)
      { s: 0, e: P, l: W, p: 0 },
      // 1. Rest at TR
      { s: P, e: P + S, l: 0, p: W },
      // 2. Move Right (TR to BR)
      { s: P + S, e: 2 * P + S, l: H, p: W },
      // 3. Rest at BR
      { s: 2 * P + S, e: 2 * P + 2 * S, l: 0, p: W + H },
      // 4. Move Bottom (BR to BL)
      { s: 2 * P + 2 * S, e: 3 * P + 2 * S, l: W, p: W + H },
      // 5. Rest at BL
      { s: 3 * P + 2 * S, e: 3 * P + 3 * S, l: 0, p: 2 * W + H },
      // 6. Move Left (BL to TL)
      { s: 3 * P + 3 * S, e: 4 * P + 3 * S, l: H, p: 2 * W + H },
      // 7. Rest at TL (Covers the final 100ms up to T_CYCLE)
      { s: 4 * P + 3 * S, e: T_CYCLE, l: 0, p: L },
    ];

    const animate = (currentTime: number): void => {
      const W = canvas.width;
      const H = canvas.height;
      const L = 2 * W + 2 * H;
      const timeElapsed = currentTime - startTimeRef.current;
      ctx.clearRect(0, 0, W, H);

      if (animationState === "PRELUDE") {
        const p = Math.min(1, timeElapsed / PRELUDE_DURATION);
        const distanceTraveled = W * p;

        renderChronoFilament(ctx, W, H, distanceTraveled, L);

        if (p >= 1) {
          setAnimationState("LOOP");
          // Fix: Offset to skip the first segment (which PRELUDE just drew)
          startTimeRef.current = currentTime - T_SEGMENT;
        }
      } else if (animationState === "LOOP") {
        const loopTime = timeElapsed % T_CYCLE;
        const phases = getPhases(W, H, L);

        let currentPhase = phases.find(
          (p) => loopTime >= p.s && loopTime < p.e,
        );

        if (currentPhase) {
          let currentPos: number;
          if (currentPhase.l > 0) {
            // MOVE Phase (accelerated)
            const normalizedTime = (loopTime - currentPhase.s) / T_SEGMENT;
            currentPos =
              currentPhase.p + easeInQuint(normalizedTime) * currentPhase.l;
          } else {
            // REST Phase (stopped)
            currentPos = currentPhase.p;
          }
          renderChronoFilament(ctx, W, H, currentPos, L);
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };

    if (
      isCanvasReady &&
      (animationState === "PRELUDE" || animationState === "LOOP")
    )
      animate(performance.now());

    // Cleanup on unmount/state change
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isCanvasReady, animationState, showLoadingOverlay]);

  // Render the canvas overlay (Blurs the content underneath via backdrop-filter)
  return showLoadingOverlay || animationState !== "IDLE" ? (
    <div className="navigation-modal-overlay">
      <div className="navigation-modal-card">
        {/* Pulsing Circles Animation */}
        <div className="pulsing-circles-container">
          {[0, 1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className={`pulse-circle ${i <= pulseStep ? 'active' : ''}`}
              style={{
                animationDelay: `${i * 0.1}s`,
              }}
            />
          ))}
        </div>
        
        {/* Playful Message */}
        <div className="navigation-message">
          <span key={currentMessage} className="message-text">
            {currentMessage || "Loading..."}
          </span>
        </div>
      </div>
    </div>
  ) : null;
};

export default LoadingOverlay;
